import { useQuery } from "@tanstack/react-query";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CalendarMonth,
  LocationOnOutlined,
  MyLocation,
  PeopleAltRounded,
  PinDrop,
  WatchLaterOutlined,
} from "@mui/icons-material";
import { db } from "../../firebase";
import { useUser } from "../../hooks";

import "./trip_infos.scss";
import { PrimaryButton } from "../../components";
import { useCallback } from "react";
import { DOWNLOAD_URL } from "../..";
import { isAndroid, isIOS } from "react-device-detect";

function formatToDoubleDigit(num: number) {
  return num < 10 ? `0${num}` : num.toString();
}

interface ILocation {
  city: string;
  region: string;
  country: string;
  alias?: string;
  // Following address is for legacy compatibility only
  address?: string;
  lat: number;
  lng: number;
}

interface TripSheetData {
  startDate: Timestamp;
  arrivalDate: Timestamp;
  returnDate?: Timestamp;
  returnArrivalDate?: Timestamp;
  sBarsCount: number;
  mBarsCount: number;
  lBarsCount: number;
  start: ILocation;
  dest: ILocation;
  destEventId: string;
  goingPoolers: string[];
  returningPoolers?: string[];
  price: number;
  roundTrip: boolean;
  seats: number;
  steps: ILocation[];
}

interface TripSheetProps {
  data: TripSheetData;
}

interface BarComponentProps {
  size: "s" | "m" | "l";
  count: number;
}

const barsAssets = {
  s: "bar-s.svg",
  m: "bar-m.svg",
  l: "bar-l.svg",
};

const BarComponent = ({ size, count }: BarComponentProps) => {
  const componentClassName = count > 1 ? "accepted" : "rejected";

  return (
    <div className={`bar-count ${componentClassName}`}>
      <img src={`/assets/${barsAssets[size]}`} alt={`Barre ${size}`} />
    </div>
  );
};

const TripSheet = ({ data }: TripSheetProps) => {
  const formattedStartDate = formatToDoubleDigit(
    data.startDate.toDate().getDate()
  );
  const formattedStartMonth = formatToDoubleDigit(
    data.startDate.toDate().getMonth() + 1
  );
  const formattedStartHour = formatToDoubleDigit(
    data.startDate.toDate().getHours()
  );
  const formattedStartMinute = formatToDoubleDigit(
    data.startDate.toDate().getMinutes()
  );

  const formattedReturnDate =
    data.returnDate && formatToDoubleDigit(data.returnDate.toDate().getDate());
  const formattedReturnMonth =
    data.returnDate &&
    formatToDoubleDigit(data.returnDate.toDate().getMonth() + 1);
  const formattedReturnHour =
    data.returnDate && formatToDoubleDigit(data.returnDate.toDate().getHours());
  const formattedReturnMinute =
    data.returnDate &&
    formatToDoubleDigit(data.returnDate.toDate().getMinutes());

  const goingSeatsLeftColor =
    data.goingPoolers.length < data.seats - 1 ? "okay" : "alert";
  const returningSeatsLeftColor =
    data.returningPoolers && data.returningPoolers.length < data.seats - 1
      ? "okay"
      : "alert";

  return (
    <div className="trip-sheet">
      <div id="route-infos">
        <div id="start-loc">
          <MyLocation />
          <p>{data.start.city}</p>
        </div>
        <div id="route-dates">
          <div id="date-go">
            <img src="/assets/trajet_aller.svg" alt="Trajet aller" />
            <div className="time">
              <div className="time-date">
                <CalendarMonth />
                <p>{[formattedStartDate, formattedStartMonth].join("/")}</p>
              </div>
              <div className="time-hour">
                <WatchLaterOutlined />
                <p>{[formattedStartHour, formattedStartMinute].join("h")}</p>
              </div>
            </div>
          </div>
          {data.steps.length > 0 && <span className="divider" />}
          {data.steps.length > 0 &&
            data.steps.map((step) => (
              <div className="step" key={step.city}>
                <PinDrop />
                <p>{step.city}</p>
              </div>
            ))}
          {data.roundTrip === true && data.returnDate !== undefined && (
            <>
              <span className="divider" />
              <div id="date-return">
                <img src="/assets/trajet_retour.svg" alt="Trajet retour" />
                <div className="time">
                  <div className="time-date">
                    <CalendarMonth />
                    <p>
                      {[formattedReturnDate, formattedReturnMonth].join("/")}
                    </p>
                  </div>
                  <div className="time-hour">
                    <WatchLaterOutlined />
                    <p>
                      {[formattedReturnHour, formattedReturnMinute].join("h")}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div id="dest-loc">
          <LocationOnOutlined />
          <p>{data.dest.alias ?? data.dest.address ?? data.dest.city}</p>
        </div>
      </div>
      <div id="pool-infos">
        {data.seats > 4 ? (
          <img src="/assets/utilitary.svg" alt="Utilitaire" id="pool-vehicle" />
        ) : (
          <img src="/assets/car.svg" alt="Voiture" id="pool-vehicle" />
        )}
        <div id="going-poolers-count">
          <div
            className={`pooler-count ${goingSeatsLeftColor}`}
            id="going-poolers"
          >
            <img src="/assets/trajet_aller.svg" alt="Trajet aller" />
            <PeopleAltRounded fontSize="large" />
            <p>{[data.goingPoolers.length, data.seats].join("/")}</p>
          </div>
          {data.roundTrip === true && data.returningPoolers !== undefined && (
            <div
              className={`pooler-count ${returningSeatsLeftColor}`}
              id="going-poolers"
            >
              <img src="/assets/trajet_retour.svg" alt="Trajet retour" />
              <PeopleAltRounded fontSize="large" />
              <p>{[data.returningPoolers.length, data.seats].join("/")}</p>
            </div>
          )}
        </div>
        <div id="returning-poolers-count"></div>
        <div id="bars">
          <BarComponent size="s" count={data.sBarsCount} />
          <BarComponent size="m" count={data.mBarsCount} />
          <BarComponent size="l" count={data.lBarsCount} />
        </div>
      </div>
    </div>
  );
};

interface UserInfosProps {
  profilePic: string;
  username: string;
  firstname: string;
}

const UserInfos = ({ profilePic, username, firstname }: UserInfosProps) => {
  return (
    <div className="user-infos">
      <div id="profile-pic">
        <img src={profilePic} alt={username} />
      </div>
      <p>{`${username} (${firstname})`}</p>
    </div>
  );
};

const TripInfos = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const nav = useNavigate();
  const location = useLocation();

  const tripQuery = useQuery({
    queryKey: ["trip", tripId],
    queryFn: async () => {
      if (!tripId) {
        nav("/");
        return undefined;
      }

      const res = await getDoc(doc(db, "Trips", tripId));

      if (!res.exists()) {
        nav("/");
        return undefined;
      }

      return res.data();
    },
  });

  const driverQuery = useUser(tripQuery.data?.driver);

  const openAppOrAppStore = useCallback(() => {
    if (!isIOS && !isAndroid) {
      window.open(DOWNLOAD_URL);
      return;
    }

    if (!window.open(`calshare://fr.swun.calshare${location.pathname}`)) {
      window.open(DOWNLOAD_URL);
    }
  }, [location.pathname]);

  return (
    <>
      <section className="part-page trip-page">
        <h1 id="title">
          Trajet pour {(tripQuery.data as TripSheetData)?.dest.address}
        </h1>
        {tripQuery.data !== undefined && (
          <TripSheet data={tripQuery.data as TripSheetData} />
        )}
        {driverQuery.data !== undefined &&
          driverQuery.data[0] !== undefined && (
            <UserInfos
              profilePic={driverQuery.data[0].profilePic}
              username={driverQuery.data[0].username}
              firstname={driverQuery.data[0].firstname}
            />
          )}
        <PrimaryButton onClick={openAppOrAppStore}>
          Je réserve ma place
        </PrimaryButton>
        <p className="link" onClick={openAppOrAppStore}>
          Voir dans l'app
        </p>
      </section>
    </>
  );
};

export default TripInfos;
export { type ILocation };
